import {
    CodeEditor,
    FormField,
    Label,
    Message,
    MessageStatus
} from "@app/form_components";
import { useUserSettings } from "@app/hooks";
import { VersionSelector } from "@app/page-components";
import {
    AiCodeGenerationDialog,
    ExplainCodePopover,
    KeyboardShortcutsPopover,
    RunScriptDialog
} from "@app/page-components/app/canvas/script";
import { ValueOrVariableType } from "@app/types";
import { stringify } from "@app/utilities";
import { copyToClipboard, modifierKey } from "@app/utilities/functions";
import { Button, Box, Modal } from "@platform/ui-library";
import { Fragment } from "react";
import BaseDialog from "./BaseDialog";

export default class CustomScriptDialog extends BaseDialog {
    /**
     * Editor dialog for editing custom function inside custom node
     * @module CustomScriptDialog
     * @param {Object} props React props.
     * @param {Array} props.arguments Function arguments for display in header.
     * @param {boolean} props.isDataNode Whether the @see node is a data node instead of a custom script.
     * @param {Object} props.node Node to modify the script of.
     * @param {(scriptKey: string, script: string) => void} props.onSave Callback to save the script.
     * @param {() => void} props.onClose Callback to close the dialog.
     * @param {boolean} props.readOnly Whether the editor is read-only.
     * @param {string} props.title Dialog title.
     */
    constructor(props) {
        super(props);
        this.onSave = this.onSave.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleOpenAiDialog = this.handleOpenAiDialog.bind(this);
        this.handleOpenRunDialog = this.handleOpenRunDialog.bind(this);
        this._editorHandle = null;

        const { node } = props;
        this.state = {
            compareSerialized: null,
            expandedInputs: useUserSettings.getState().expandedInputs,
            isAiDialogOpen: false,
            isExplainOpen: false,
            isRunDialogOpen: false,
            script: this.getScript(node)
        };
    }

    getScript(node) {
        const { config } = node.props;
        return (this.props.isDataNode) ? config.customScript : config.function.script;
    }

    async onSave() {
        // Beautify before saving so the stored code is always formatted
        const script = (this._editorHandle) ? (
            await this._editorHandle.beautify()
        ) : this.state.script;
        const { isDataNode, node } = this.props;
        const nodeId = node.id;
        const originalScript = this.getScript(node);

        if (script !== originalScript) {
            const scriptKey = (isDataNode) ? 'customScript' : 'script';
            this.props.onSave(scriptKey, script);

            if (nodeId) {
                window.apm.postEventToCurrentView({
                    target: "request-refresh",
                    event: { id: nodeId, type: "node" }
                });
            }
        }
        this.handleClose();
    }

    handleClose() {
        this.props.onClose?.();
    }

    componentDidMount() {
        super.componentDidMount();
        document.addEventListener("ev-dialog-add", this.onSave);

        this._unsubscribeUserSettings = useUserSettings.subscribe((state, prevState) => {
            if (state.expandedInputs !== prevState.expandedInputs) {
                this.setState({ expandedInputs: state.expandedInputs });
            }
        });
    }

    unregister() {
        document.removeEventListener("ev-dialog-add", this.onSave);
        if (this._unsubscribeUserSettings) {
            this._unsubscribeUserSettings();
            this._unsubscribeUserSettings = null;
        }
    }

    /**
     * Open the AI code generation dialog
     */
    handleOpenAiDialog() {
        this._editorHandle?.closeMaximized();
        this.setState({ isAiDialogOpen: true });
    }

    /**
     * Open the Run Script dialog
     */
    handleOpenRunDialog() {
        this._editorHandle?.closeMaximized();
        this.setState({ isRunDialogOpen: true });
    }

    async copyToClipboard(text, event) {
        const el = event.currentTarget;
        await copyToClipboard(text);
        el.classList.add("copied");
        setTimeout(() => el.classList.remove("copied"), 1500);
    }

    /**
     * Get the function "closure" part for display, including output variable bindings.
     * @returns {React.ReactNode}
     */
    getScriptFooter() {
        const { config } = this.props.node.props;
        const outputs = config.function?.outputs ?? config.outputs ?? [];
        const outputVars = outputs.filter((o) => o.varName).map((o) => o.varName);

        if (!outputVars.length) {
            return "}";
        }

        const outputElements = outputVars.map((varName, index) => {
            return (
                <Fragment key={`out-${index}`}>
                    {(index === 0) ? null : ", "}
                    <span
                        className="variable-binding"
                        data-tooltip={varName}
                        onClick={(e) => this.copyToClipboard(varName, e)}
                    >
                        {varName}
                    </span>
                </Fragment>
            );
        });

        return (
            <>
                {"} outputs: "}
                {outputElements}
            </>
        );
    }

    /**
     * Get the function "argument" part for display as a React element with styled variable bindings
     * @returns {React.ReactNode}
     */
    getScriptHeader() {
        const args = this.props.arguments ?? [];
        if (!args.length) {
            return "function () {";
        }

        const { expandedInputs } = this.state;
        const hasVariableBindings = args.some((p) => {
            return p.type === ValueOrVariableType.variable && p.varName;
        });

        return <>
            {"function ("}
            {args.map((p, index) => {
                let argumentName = p.argumentName;
                let argumentValue = null;
                if (p.type === ValueOrVariableType.value) {
                    argumentValue = ` = ${p.value}`;
                } else if (p.varName) {
                    argumentName = (
                        <span
                            className="variable-binding"
                            data-tooltip={p.argumentName}
                            onClick={(e) => this.copyToClipboard(p.argumentName, e)}
                        >
                            {p.argumentName}
                        </span>
                    );
                    argumentValue = (!expandedInputs) ? null : ` = {{${p.varName}}}`;
                }
                return (
                    <Fragment key={index}>
                        {(index === 0) ? null : ', '}
                        {argumentName}
                        {argumentValue}
                    </Fragment>
                );
            })}
            {") {"}
            {(!hasVariableBindings) ? null : (
                <span
                    key="toggle"
                    style={{ marginLeft: 'var(--space-small)', verticalAlign: 'middle' }}
                >
                    <Button
                        icon={() => <i className={`fe ${expandedInputs ? 'icon-minimize-2' : 'icon-maximize-2'}`} />}
                        onClick={() => useUserSettings.getState().toggleExpandedInputs()}
                        style={{ '--square-size': '1.5em' }}
                        theme={['outline', 'small', 'round']}
                        title={expandedInputs ? `Collapse inputs (${modifierKey}I)` : `Expand inputs (${modifierKey}I)`}
                        type="button"
                    />
                </span>
            )}
        </>;
    }

    render() {
        const { serialized } = window.apm;
        const { isDataNode, node, readOnly } = this.props;
        const {
            compareSerialized,
            isAiDialogOpen,
            isExplainOpen,
            isRunDialogOpen,
            script
        } = this.state;
        const compareNode = compareSerialized?.components.objects.find((object) => {
            return object.id === node.props.parentComponentID ?? node.id;
        });

        // Get function arguments for AI context
        const functionInputs = this.props.arguments ?? [];
        const toolbarActions = (
            <>
                <div style={{ position: 'relative' }}>
                    <Button
                        icon={() => <i className="fe icon-file-text" />}
                        onClick={() => this.setState((prev) => ({ isExplainOpen: !prev.isExplainOpen }))}
                        theme="outline"
                        title={`Explain Code (${modifierKey}J)`}
                        type="button"
                    >
                        Explain
                    </Button>
                    <ExplainCodePopover
                        currentCode={script}
                        functionInputs={functionInputs}
                        isOpen={isExplainOpen}
                        onClose={() => this.setState((prev) => ({ isExplainOpen: !prev.isExplainOpen }))}
                    />
                </div>
                {!readOnly && (
                    <Button
                        icon={() => <i className="fe icon-cpu" />}
                        onClick={this.handleOpenAiDialog}
                        theme="outline"
                        title={`AI Code Assistant (${modifierKey}H)`}
                        type="button"
                    >
                        AI Assist
                    </Button>
                )}
                <Button
                    icon={() => <i className="fe icon-play" />}
                    onClick={this.handleOpenRunDialog}
                    theme="outline"
                    title={`Run Script (${modifierKey}R)`}
                    type="button"
                >
                    Run
                </Button>
            </>
        );

        return (
            <Modal
                className="ui-box-medium"
                handleClose={() => null}
                isOpen
            >
                <Box theme="noPadding">
                    <div className="box header">
                        <h2>{this.props.title}</h2>
                    </div>
                    <div className="modal-inner flex-column flex-gap" style={{ minHeight: '100%' }}>
                        <div className="flex-row flex-gap" style={{ alignItems: 'flex-end' }}>
                            <FormField className="z-over-code-editor flex-1" label="Compare Against:">
                              <VersionSelector
                                branchId={serialized.branchId}
                                isClearable
                                onChange={(_, option) => {
                                    this.setState({ compareSerialized: option?.serialized ?? null });
                                }}
                                value={compareSerialized?.version ?? null}
                              />
                            </FormField>
                        </div>
                {(!compareSerialized || compareNode) ? null : (
                    <Message
                        message="Unable to find matching object in selected version."
                        status={MessageStatus.error}
                    />
                )}
                {(!compareNode || isDataNode) ? null : (
                    <>
                        <Label>Inputs</Label>
                        <div className="script-editor-container">
                            <CodeEditor
                                originalValue={stringify(compareNode.props.config.inputs ?? [])}
                                readOnly
                                value={stringify(node.props.config.inputs ?? [])}
                            />
                        </div>
                        <Label>Outputs</Label>
                        <div className="script-editor-container">
                            <CodeEditor
                                originalValue={stringify(compareNode.props.config.outputs ?? [])}
                                readOnly
                                value={stringify(node.props.config.outputs ?? [])}
                            />
                        </div>
                    </>
                )}
                <div className="script-editor-container">
                    <CodeEditor
                        additionalActions={toolbarActions}
                        editorRef={(handle) => { this._editorHandle = handle; }}
                        header={this.getScriptHeader()}
                        onAiAssist={!readOnly ? this.handleOpenAiDialog : undefined}
                        onExplainCode={() => this.setState((prev) => ({ isExplainOpen: !prev.isExplainOpen }))}
                        onRunScript={this.handleOpenRunDialog}
                        onSave={!readOnly ? this.onSave : undefined}
                        onToggleInputs={() => useUserSettings.getState().toggleExpandedInputs()}
                        originalValue={(compareNode) ? this.getScript(compareNode) : undefined}
                        onChange={(script) => this.setState({ script })}
                        readOnly={readOnly}
                        value={script}
                    />
                </div>
                <div className="bold">
                    {this.getScriptFooter()}
                </div>
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 'var(--space)',
                        justifyContent: 'flex-end'
                    }}
                >
                    <div style={{ marginRight: 'auto' }}>
                        <KeyboardShortcutsPopover readOnly={readOnly} />
                    </div>
                    <Button onClick={this.handleClose} theme="secondary" type="button">
                        {window.l.popup.close}
                    </Button>
                    {!readOnly && (
                        <Button onClick={this.onSave} type="button">
                            {window.l.popup.save}
                        </Button>
                    )}
                </div>
                
                {/* AI Code Generation Dialog */}
                <AiCodeGenerationDialog
                    currentCode={script ?? ''}
                    functionInputs={functionInputs}
                    isOpen={isAiDialogOpen}
                    onAccept={(generatedCode) => this.setState({ script: generatedCode })}
                    onClose={() => this.setState({ isAiDialogOpen: false })}
                />

                {/* Run Script Dialog */}
                <RunScriptDialog
                    functionArguments={functionInputs}
                    isOpen={isRunDialogOpen}
                    nodeName={this.props.node.name}
                    onClose={() => this.setState({ isRunDialogOpen: false })}
                    script={script}
                />
                    </div>
                </Box>
            </Modal>
        );
    }
}